import { useState } from 'react';
import PropTypes from 'prop-types';
import { CalendarIcon } from 'lucide-react';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import Calendar from '#/modules/commonComponents/form/Calendar';
import { Input } from '#/modules/commonComponents/form/Input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '#/modules/commonComponents/popover';
import {
  apiDateToDate,
  dateToApiDate,
  formatApiDateDisplay,
  formatApiDateRangeDisplay,
  isValidApiDate,
  parseUserDateRangeInput,
  parseUserDateToApi,
} from '#/utils/date';

function resolveCalendarMonth(mode, value) {
  if (mode === 'range') {
    const from = apiDateToDate(value?.from);
    const to = apiDateToDate(value?.to);
    return from ?? to ?? new Date();
  }
  return apiDateToDate(value) ?? new Date();
}

function toDraftFromValue(mode, value) {
  if (mode === 'range') {
    return formatApiDateRangeDisplay(value ?? {});
  }
  return formatApiDateDisplay(value) || '';
}

function toPendingRangeFromValue(value) {
  const from = apiDateToDate(value?.from);
  const to = apiDateToDate(value?.to);

  if (!from && !to) {
    return undefined;
  }

  return { from, to };
}

function isCompleteApiDateRange({ from, to } = {}) {
  return isValidApiDate(from) && isValidApiDate(to);
}

/**
 * 受控日期輸入：手打 + Calendar Popover。
 * single：value = API `YYYY-MM-DD` 或 ''；
 * range：value = `{ from, to }`（API 字串）。
 *
 * commitBehavior：
 * - instant（預設 single）：日曆選取即 onChange 並關閉
 * - deferred（預設 range）：日曆僅暫選；完整區間須按「確認」才 onChange
 */
const DateInputCore = ({
  id,
  mode = 'single',
  value,
  onChange,
  onBlur,
  placeholder = '西元年/月/日',
  disabled = false,
  readOnly = false,
  inputClassName = '',
  className = '',
  ariaInvalid = false,
  compact = false,
  commitBehavior,
  numberOfMonths,
}) => {
  const resolvedCommitBehavior = commitBehavior ?? (mode === 'range' ? 'deferred' : 'instant');
  const resolvedNumberOfMonths = numberOfMonths ?? (mode === 'range' ? 2 : 1);
  const isDeferredRange = mode === 'range' && resolvedCommitBehavior === 'deferred';

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => toDraftFromValue(mode, value));
  const [prevValueKey, setPrevValueKey] = useState('');
  const [pendingRange, setPendingRange] = useState(undefined);
  const [calendarMonth, setCalendarMonth] = useState(() => resolveCalendarMonth(mode, value));

  const valueKey = mode === 'range'
    ? `${value?.from ?? ''}|${value?.to ?? ''}`
    : (value ?? '');

  if (valueKey !== prevValueKey) {
    setPrevValueKey(valueKey);
    setDraft(toDraftFromValue(mode, value));
  }

  function resetPendingRange() {
    setPendingRange(undefined);
  }

  function handlePopoverOpenChange(nextOpen) {
    if (nextOpen) {
      setCalendarMonth(resolveCalendarMonth(mode, value));
      if (isDeferredRange) {
        setPendingRange(toPendingRangeFromValue(value));
      }
      setOpen(true);
      return;
    }

    if (isDeferredRange) {
      resetPendingRange();
    }
    setOpen(false);
  }

  function commitDraft(nextDraft) {
    const trimmed = String(nextDraft ?? '').trim();
    if (!trimmed) {
      if (mode === 'range') {
        onChange?.({ from: undefined, to: undefined });
      } else {
        onChange?.('');
      }
      setDraft('');
      return true;
    }

    if (mode === 'range') {
      const parsed = parseUserDateRangeInput(trimmed);
      if (parsed.mode === 'single' && parsed.from) {
        onChange?.({ from: parsed.from, to: parsed.from });
        setDraft(formatApiDateRangeDisplay({ from: parsed.from, to: parsed.from }));
        return true;
      }
      if (parsed.mode === 'range' && parsed.from && parsed.to) {
        onChange?.({ from: parsed.from, to: parsed.to });
        setDraft(formatApiDateRangeDisplay({ from: parsed.from, to: parsed.to }));
        return true;
      }
      return false;
    }

    const api = parseUserDateToApi(trimmed);
    if (!api) {
      return false;
    }
    onChange?.(api);
    setDraft(formatApiDateDisplay(api));
    return true;
  }

  function handleBlur(event) {
    commitDraft(draft);
    onBlur?.(event);
  }

  function handleKeyDown(event) {
    event.stopPropagation?.();
    if (event.key === 'Enter') {
      event.preventDefault();
      commitDraft(draft);
    }
  }

  function commitInstantRange(from, to) {
    if (!from) {
      return;
    }
    const resolvedTo = to || from;
    onChange?.({ from, to: resolvedTo });
    setDraft(formatApiDateRangeDisplay({ from, to: resolvedTo }));
    if (to) {
      setOpen(false);
    }
  }

  function handleDeferredConfirm() {
    const from = dateToApiDate(pendingRange?.from);
    const to = dateToApiDate(pendingRange?.to);

    if (!isCompleteApiDateRange({ from, to })) {
      resetPendingRange();
      setOpen(false);
      return;
    }

    onChange?.({ from, to });
    setDraft(formatApiDateRangeDisplay({ from, to }));
    resetPendingRange();
    setOpen(false);
  }

  function handleDeferredCancel() {
    resetPendingRange();
    setOpen(false);
  }

  const selectedSingle = mode === 'single' ? apiDateToDate(value) : undefined;
  const selectedRange = mode === 'range'
    ? (isDeferredRange ? pendingRange : {
      from: apiDateToDate(value?.from),
      to: apiDateToDate(value?.to),
    })
    : undefined;

  return (
    <div className={cn('relative flex w-full items-center gap-1', className)}>
      <Input
        id={id}
        type="text"
        value={draft}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        aria-invalid={ariaInvalid || undefined}
        className={cn(
          compact ? 'h-7 pr-8 text-xs' : 'pr-10',
          inputClassName,
          readOnly && 'readonly',
        )}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      <Popover open={open} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled || readOnly}
            className={cn(
              'absolute right-0.5 top-1/2 -translate-y-1/2 text-muted-foreground',
              compact ? 'size-6' : 'size-8',
            )}
            aria-label="開啟日曆"
            onClick={(event) => event.stopPropagation()}
          >
            <CalendarIcon className={compact ? 'size-3.5' : 'size-4'} aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-auto p-0"
          onClick={(event) => event.stopPropagation()}
        >
          {mode === 'range' ? (
            <>
              <Calendar
                mode="range"
                month={calendarMonth}
                onMonthChange={setCalendarMonth}
                selected={selectedRange}
                onSelect={(range) => {
                  if (isDeferredRange) {
                    setPendingRange(range);
                    return;
                  }
                  const from = dateToApiDate(range?.from);
                  const to = dateToApiDate(range?.to);
                  commitInstantRange(from, to);
                }}
                numberOfMonths={resolvedNumberOfMonths}
                // 多月並排時隱藏前後月日期，避免同一天在兩個面板各出現一次（選取看起來像 3 個點）
                showOutsideDays={resolvedNumberOfMonths === 1}
              />
              {isDeferredRange ? (
                <div className="flex justify-end gap-2 border-t border-slate-200 p-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDeferredCancel}
                  >
                    取消
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleDeferredConfirm}
                  >
                    確認
                  </Button>
                </div>
              ) : null}
            </>
          ) : (
            <Calendar
              mode="single"
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              selected={selectedSingle}
              onSelect={(date) => {
                const api = dateToApiDate(date);
                if (!api) {
                  return;
                }
                onChange?.(api);
                setDraft(formatApiDateDisplay(api));
                setOpen(false);
              }}
              numberOfMonths={resolvedNumberOfMonths}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

DateInputCore.propTypes = {
  id: PropTypes.string,
  mode: PropTypes.oneOf(['single', 'range']),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      from: PropTypes.string,
      to: PropTypes.string,
    }),
  ]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  inputClassName: PropTypes.string,
  className: PropTypes.string,
  ariaInvalid: PropTypes.bool,
  compact: PropTypes.bool,
  commitBehavior: PropTypes.oneOf(['instant', 'deferred']),
  numberOfMonths: PropTypes.number,
};

export default DateInputCore;
